import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Users, FileText, Calendar, FolderOpen, Mail, LogOut, Menu, X, UserCog, Settings, FileEdit, Link as LinkIcon, Vote } from 'lucide-react';
import { clearAdminSession, getAdminRole } from './utils/adminAuth';
import MembersManager from './MembersManager';
import NewsManager from './NewsManager';
import EventsManager from './EventsManager';
import ResourcesManager from './ResourcesManager';
import ContactsManager from './ContactsManager';
import LeadershipManager from './LeadershipManager';
import ContactInfoManager from './ContactInfoManager';
import PageContentManager from './PageContentManager';
import AffiliatesManager from './AffiliatesManager';
import ElectionManager from './ElectionManager';
import DashboardHome from './DashboardHome';

import AdminUsersManager from './AdminUsersManager';

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    clearAdminSession();
    navigate('/admin/login');
  };

  const role = getAdminRole();
  const isSuperAdmin = role === 'super_admin';

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: Menu },
    ...(isSuperAdmin
      ? [
          { path: '/admin/page-content', label: 'Page Content', icon: FileEdit },
          { path: '/admin/admin-users', label: 'Admin Users', icon: UserCog },
        ]
      : []),
    { path: '/admin/members', label: 'Members', icon: Users },
    { path: '/admin/elections', label: 'Elections', icon: Vote },
    { path: '/admin/news', label: 'News', icon: FileText },
    { path: '/admin/events', label: 'Events', icon: Calendar },
    { path: '/admin/resources', label: 'Resources', icon: FolderOpen },
    { path: '/admin/contacts', label: 'Contacts', icon: Mail },
    { path: '/admin/leadership', label: 'Leadership', icon: UserCog },
    { path: '/admin/affiliates', label: 'Affiliates', icon: LinkIcon },
    { path: '/admin/contact-info', label: 'Contact Info', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 lg:relative lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_png-emp-assoc/artifacts/df9awda4_PEA%20Logo" 
                  alt="PEA PNG Logo" 
                  className="h-10 w-auto object-contain"
                />
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Admin Panel</h2>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-gray-600 hover:text-gray-900"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                        isActive(item.path)
                          ? 'bg-orange-500 text-white shadow-md'
                          : 'text-gray-700 hover:bg-orange-50 hover:text-orange-500'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 w-full transition-colors"
            >
              <LogOut size={20} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
              data-testid="open-sidebar-btn"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-600 font-medium"
              >
                View Website
              </a>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
          <Routes>
            <Route path="/dashboard" element={<DashboardHome />} />
            {isSuperAdmin && <Route path="/page-content" element={<PageContentManager />} />}
            {isSuperAdmin && <Route path="/admin-users" element={<AdminUsersManager />} />}
            <Route path="/members" element={<MembersManager />} />
            <Route path="/elections" element={<ElectionManager />} />
            <Route path="/news" element={<NewsManager />} />
            <Route path="/events" element={<EventsManager />} />
            <Route path="/resources" element={<ResourcesManager />} />
            <Route path="/contacts" element={<ContactsManager />} />
            <Route path="/leadership" element={<LeadershipManager />} />
            <Route path="/affiliates" element={<AffiliatesManager />} />
            <Route path="/contact-info" element={<ContactInfoManager />} />
            <Route path="/" element={<DashboardHome />} />
          </Routes>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default AdminDashboard;